import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth";
import { Toaster } from "./components/ui/sonner";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import LoginPage from "./pages/LoginPage";
import AdminLoginPage from "./pages/AdminLoginPage";
import RegisterPage from "./pages/RegisterPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import ClientPortalPage from "./pages/ClientPortalPage";
import ShortLinkPage from "./pages/ShortLinkPage";
import DashboardPage from "./pages/DashboardPage";
import ClientDetailPage from "./pages/ClientDetailPage";
import AdminPage from "./pages/AdminPage";
import PricingPage from "./pages/PricingPage";
import ProfilePage from "./pages/ProfilePage";
import AdminProfilePage from "./pages/AdminProfilePage";
import AdminSettingsPage from "./pages/AdminSettingsPage";
import SettingsPage from "./pages/SettingsPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import InviteEarnPage from "./pages/InviteEarnPage";
import AuthCallback from "./pages/AuthCallback";
import { useEffect } from "react";
import { requestNotificationPermission, onForegroundMessage } from "./lib/firebase";
import { notificationsAPI } from "./lib/api";
import { toast } from "sonner";
import "./App.css";

// Notification setup hook
function NotificationSetup() {
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (!isAuthenticated) return;
    // Ask for notification permission after login (with delay)
    const timer = setTimeout(async () => {
      try {
        const token = await requestNotificationPermission();
        if (token) {
          await notificationsAPI.registerToken(token);
        }
      } catch (err) {
        // Silent fail — notifications are optional
      }
    }, 5000);

    // Listen for foreground notifications
    const unsub = onForegroundMessage((payload) => {
      toast.info(payload.notification?.title || "New Notification", {
        description: payload.notification?.body,
      });
    });

    return () => { clearTimeout(timer); if (typeof unsub === 'function') unsub(); };
  }, [isAuthenticated]);

  return null;
}

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// Public Route Component (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Detect Google OAuth callback — session_id in URL hash
function OAuthDetector({ children }) {
  const hash = window.location.hash;
  if (hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return children;
}

function AppRoutes() {
  return (
    <OAuthDetector>
    <Routes>
      <Route path="/" element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/login"
        element={<Navigate to="/" replace />}
      />
      <Route
        path="/admin-login"
        element={
          <PublicRoute>
            <AdminLoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/sign-up"
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={<Navigate to="/sign-up" replace />}
      />
      <Route
        path="/verify"
        element={<VerifyEmailPage />}
      />
      <Route
        path="/client-portal"
        element={<ClientPortalPage />}
      />
      <Route
        path="/p/:code"
        element={<ShortLinkPage />}
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients/:clientId"
        element={
          <ProtectedRoute>
            <ClientDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pricing"
        element={
          <ProtectedRoute>
            <PricingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin-profile"
        element={
          <ProtectedRoute>
            <AdminProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <ProtectedRoute>
            <AnalyticsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/invite"
        element={
          <ProtectedRoute>
            <InviteEarnPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin-settings"
        element={
          <ProtectedRoute>
            <AdminSettingsPage />
          </ProtectedRoute>
        }
      />
    </Routes>
    </OAuthDetector>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="min-h-screen bg-background">
          <NotificationSetup />
          <AppRoutes />
          <Toaster position="top-center" richColors />
          <PWAInstallPrompt />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
